import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import ipponApi from "../config/axios";
import type { ManagementTaskType } from "./managementTaskSlice";

const BaseURL = import.meta.env.VITE_API_URL;

export type ManagementTaskCustomFieldType =
  | "text"
  | "number"
  | "date"
  | "select"
  | "checkbox"
  | "textarea";

export type ManagementTaskCustomFieldSection = "task" | "result";

export interface ManagementTaskCustomFieldRecord {
  client_id: string;
  field_id: string;
  label: string;
  field_type: ManagementTaskCustomFieldType;
  section_key: ManagementTaskCustomFieldSection;
  options?: string[];
  applies_to_task_types?: ManagementTaskType[];
  display_order?: number;
  created_at: string;
  updated_at?: string;
}

export interface CreateManagementTaskCustomFieldBody {
  label: string;
  field_type: ManagementTaskCustomFieldType;
  section_key: ManagementTaskCustomFieldSection;
  options?: string[];
  applies_to_task_types?: ManagementTaskType[];
  display_order?: number;
}

export interface UpdateManagementTaskCustomFieldBody {
  label?: string;
  field_type?: ManagementTaskCustomFieldType;
  section_key?: ManagementTaskCustomFieldSection;
  options?: string[];
  applies_to_task_types?: ManagementTaskType[];
  display_order?: number;
}

interface State {
  list: {
    data: ManagementTaskCustomFieldRecord[];
    loading: boolean;
    error: string | null;
    loaded: boolean;
  };
}

const initialState: State = {
  list: { data: [], loading: false, error: null, loaded: false },
};

export const fetchManagementTaskCustomFields = createAsyncThunk<
  ManagementTaskCustomFieldRecord[],
  void
>("managementTaskCustomFields/fetch", async (_, { rejectWithValue }) => {
  try {
    const res = await ipponApi.get(
      `${BaseURL}/api/v1/management-task-custom-fields`,
    );
    return res.data?.data?.items || [];
  } catch {
    return rejectWithValue("カスタムフィールドの取得に失敗しました");
  }
});

export const createManagementTaskCustomField = createAsyncThunk<
  ManagementTaskCustomFieldRecord,
  CreateManagementTaskCustomFieldBody
>("managementTaskCustomFields/create", async (body, { rejectWithValue }) => {
  try {
    const res = await ipponApi.post(
      `${BaseURL}/api/v1/management-task-custom-fields`,
      body,
    );
    return res.data.data;
  } catch {
    return rejectWithValue("カスタムフィールドの登録に失敗しました");
  }
});

export const updateManagementTaskCustomField = createAsyncThunk<
  ManagementTaskCustomFieldRecord,
  { field_id: string; body: UpdateManagementTaskCustomFieldBody }
>(
  "managementTaskCustomFields/update",
  async ({ field_id, body }, { rejectWithValue }) => {
    try {
      const res = await ipponApi.put(
        `${BaseURL}/api/v1/management-task-custom-fields/${field_id}`,
        body,
      );
      return res.data.data;
    } catch {
      return rejectWithValue("カスタムフィールドの更新に失敗しました");
    }
  },
);

export const deleteManagementTaskCustomField = createAsyncThunk<
  { field_id: string },
  string
>(
  "managementTaskCustomFields/delete",
  async (field_id, { rejectWithValue }) => {
    try {
      await ipponApi.delete(
        `${BaseURL}/api/v1/management-task-custom-fields/${field_id}`,
      );
      return { field_id };
    } catch {
      return rejectWithValue("カスタムフィールドの削除に失敗しました");
    }
  },
);

const slice = createSlice({
  name: "managementTaskCustomFields",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchManagementTaskCustomFields.pending, (state) => {
        state.list.loading = true;
        state.list.error = null;
      })
      .addCase(fetchManagementTaskCustomFields.fulfilled, (state, action) => {
        state.list.loading = false;
        state.list.data = action.payload;
        state.list.loaded = true;
      })
      .addCase(fetchManagementTaskCustomFields.rejected, (state, action) => {
        state.list.loading = false;
        state.list.error = (action.payload as string) || null;
      })
      .addCase(createManagementTaskCustomField.fulfilled, (state, action) => {
        state.list.data.push(action.payload);
      })
      .addCase(updateManagementTaskCustomField.fulfilled, (state, action) => {
        const idx = state.list.data.findIndex(
          (f) => f.field_id === action.payload.field_id,
        );
        if (idx >= 0) state.list.data[idx] = action.payload;
      })
      .addCase(deleteManagementTaskCustomField.fulfilled, (state, action) => {
        state.list.data = state.list.data.filter(
          (f) => f.field_id !== action.payload.field_id,
        );
      });
  },
});

export default slice.reducer;
